import { COLLECTION_TYPES } from './collectionTypes.js';

/**
 * Return all subscriptions in the bound Firestore that the given user belongs
 * to, bucketed by role in the subscription. Uses array-contains queries, so
 * the user's membership is computed cross-field:
 *   - administrator: `administrators` array
 *   - seatholder:    `seatHolders` array (legacy; kept for BZW-240)
 *   - teacher:       `teachers` array
 *   - student:       `students` array
 *
 * Pure: no caching or store writes. Wrappers add those per app.
 *
 * @param {object} params
 * @param {(data: any, collection: string) => Promise<any>} params.getEntitiesWhere
 * @param {string} params.uid
 * @param {boolean} [params.noCache]
 * @returns {Promise<{administrator: any[], seatholder: any[], teacher: any[], student: any[]}>}
 */
export async function fetchUserSubscriptions({ getEntitiesWhere, uid, noCache = false }) {
    if (!uid) {
        return { administrator: [], seatholder: [], teacher: [], student: [] };
    }

    const makeQuery = (field) => getEntitiesWhere({
        needles: [{ field, operator: 'array-contains', match: uid }],
        noCache,
    }, COLLECTION_TYPES.SUBSCRIPTIONS).catch(err => {
        console.warn(`fetchUserSubscriptions(${field})`, err);
        return null;
    });

    const [subsSeat, subsAdmin, subsTeacher, subsStudent] = await Promise.all([
        makeQuery('seatHolders'),
        makeQuery('administrators'),
        makeQuery('teachers'),
        makeQuery('students'),
    ]);

    const transform = subs => (subs && !subs.empty)
        ? subs.docs.map(s => ({ ...s.data(), id: s.id }))
        : [];

    return {
        administrator: transform(subsAdmin),
        seatholder: transform(subsSeat),
        teacher: transform(subsTeacher),
        student: transform(subsStudent),
    };
}
